export interface QualitativeAssessResponse {
  passed: boolean;
  feedback: string;
  gap: string | null;
  knowledgePointId: string;
  masteryDisplay: number;
}

export class QualitativeAssessResponseError extends Error {
  constructor() {
    super("Invalid qualitative assessment response.");
    this.name = "QualitativeAssessResponseError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function hasNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/** Parse the server-owned qualitative mastery decision before it reaches UI. */
export function parseQualitativeAssessResponse(
  value: unknown,
): QualitativeAssessResponse {
  if (
    !isRecord(value) ||
    typeof value.passed !== "boolean" ||
    typeof value.feedback !== "string" ||
    (value.gap !== null && typeof value.gap !== "string") ||
    typeof value.knowledge_point_id !== "string" ||
    !isFiniteNumber(value.mastery_display)
  ) {
    throw new QualitativeAssessResponseError();
  }

  return {
    passed: value.passed,
    feedback: value.feedback,
    gap: value.gap,
    knowledgePointId: value.knowledge_point_id,
    masteryDisplay: value.mastery_display,
  };
}

/** A written question linked to a knowledge point is protected by the mastery gate. */
export function isQualitativeGateQuestion(
  normalizedQuestionType: string,
  knowledgePointId: unknown,
): boolean {
  return (
    normalizedQuestionType === "written" &&
    hasNonEmptyString(knowledgePointId)
  );
}

/** Only linked book written questions may use the server-side mastery gate. */
export function isQualitativeAssessEligible(
  normalizedQuestionType: string,
  knowledgePointId: unknown,
  bookId: unknown,
): boolean {
  return (
    isQualitativeGateQuestion(normalizedQuestionType, knowledgePointId) &&
    hasNonEmptyString(bookId)
  );
}

/** Protect model answers until a linked written question has passed assessment. */
export function canRevealQuizAnswer(
  normalizedQuestionType: string,
  knowledgePointId: unknown,
  passedQualitativeAssessment: boolean,
): boolean {
  return (
    !isQualitativeGateQuestion(normalizedQuestionType, knowledgePointId) ||
    passedQualitativeAssessment
  );
}

/** User-visible error copy key without exposing protected API error details. */
export function qualitativeAssessFailureMessage(error: unknown): string {
  if (isRecord(error) && error.status === 422) {
    return "Your answer is required.";
  }
  if (isRecord(error) && error.code === "invalid-response") {
    return "Assessment response was invalid. Please try again.";
  }
  return "Assessment could not be completed. Please try again.";
}
